'use client';
import cx from 'classnames';
import {useCallback, useEffect, useRef, useState} from 'react';
import {Button} from '../atoms/Button';
import {useIsMediumAndUp} from '../hooks/useMatchQuery';
import {charcoal} from '../sass/base/_exports.sass';
import {MoreIcon} from '../svg/MoreIcon';
import {ActionSheetDrawer} from './ActionSheetDrawer';

export const ClickOpenMenu = (props) => {
  const isMediumAndUp = useIsMediumAndUp();
  const isControlled = props.isOpen !== undefined;
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = isControlled ? props.isOpen : internalIsOpen;

  const title = props.title || 'Options';
  const menuId = props.menuId || '';
  const clickOpenMenuRef = useRef(null);
  const menuOptionsRef = useRef(null);

  const handleClose = useCallback(() => {
    props.onClose?.();
    if (!isControlled) {
      setInternalIsOpen(false);
    }
  }, [isControlled, props.onClose]);

  const handleOpen = useCallback(() => {
    props.onOpen?.();
    if (!isControlled) {
      setInternalIsOpen(true);
    }
  }, [isControlled, props.onOpen]);

  const toggleMenu = useCallback(() => {
    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [isOpen, handleClose, handleOpen]);

  const escHandler = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleOutsideClick = useCallback(
    (e) => {
      if (menuOptionsRef.current && !menuOptionsRef.current.contains(e.target)) {
        handleClose();
      }
    },
    [handleClose]
  );

  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener('keydown', escHandler);
    window.addEventListener('mousedown', handleOutsideClick);
    window.dataLayer.push({event: 'overlayModalImpression'});

    return () => {
      window.removeEventListener('keydown', escHandler);
      window.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen, escHandler, handleOutsideClick]);

  return (
    <div className="clickOpenMenu" ref={clickOpenMenuRef}>
      <Button
        data-qa-id={props.qaId || 'clickOpenMenu'}
        onClick={toggleMenu}
        variant={props.variant || 'secondary'}
        className={cx('more-options', {'menu-open': !props.trigger && isOpen})}
        size={props.trigger ? props.buttonSize || 'small' : undefined}
        ariaLabel="More options"
      >
        {props.trigger ?? <MoreIcon color={charcoal} ratio={props.iconRatio || 0.5} />}
      </Button>
      {isOpen && isMediumAndUp && (
        <ol className="clickOpenMenu__options" ref={menuOptionsRef}>
          {props.children}
        </ol>
      )}
      {isOpen && !isMediumAndUp && (
        <ActionSheetDrawer
          isOpen={isOpen}
          onClose={handleClose}
          menuId={`responsive-menu ${menuId}`}
          title={title || null}
          showCancel={false}
        >
          {props.children}
        </ActionSheetDrawer>
      )}
    </div>
  );
};
